import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'
import { cancelIdleCallback, requestIdleCallback } from '@/lib/request-idle'

export type NotificationVariant = 'default' | 'success' | 'error' | 'info' | 'warning'

export interface AppNotification {
  key: string
  message: string
  variant: NotificationVariant
  persist?: boolean
  autoHideDuration?: number
  /** Optional action button shown in the toast */
  action?: { label: string; onClick: () => void }
  /** Called when the notification is cleared (dismissed, displaced, or expired) */
  onClear?: () => void
  /** Toasts in the same group arriving within MERGE_WINDOW_MS collapse into one "(+N more)" update */
  mergeGroup?: string
}

export interface ToastOptions {
  mergeGroup?: string
}

interface NotificationState {
  current: AppNotification | null
  showNotification: (notification: AppNotification) => void
  showSuccess: (message: string, options?: ToastOptions) => void
  showError: (message: string, props?: Partial<AppNotification>) => void
  showInfo: (message: string, options?: ToastOptions) => void
  showWarning: (message: string, options?: ToastOptions) => void
  clearNotification: () => void
}

let notificationCounter = 0

// ─── error_shown telemetry ───
//
// showError is one choke point behind 118 call sites: every handled-but-
// still-broken case the user actually sees. Reported as a product event so
// it joins the same person as their crashes and funnels. Bounded like the
// exception filter: dedup per key, hard cap per session.

/** Per-session ceiling on distinct error toasts reported. */
export const MAX_ERROR_SHOWN_PER_SESSION = 50

let errorsReported = 0
const errorKeysSeen = new Set<string>()

/** Most symbols reported on one error_shown event. */
const MAX_SYMBOLS_PER_EVENT = 20

// A ticker token: 1-5 capitals, an optional stored-case marker (ABRpD, AIIAr)
// and an optional class suffix (BRK.B). Plain words like "Not" never match.
const SYMBOL_PATTERN = /\$?\b[A-Z]{1,5}(?:[a-z][A-Z]?)?(?:\.[A-Z])?\b/g

/**
 * Toast text to a low-cardinality key. Ticker symbols and numbers vary per
 * user and would both fragment grouping and leak what they were looking at.
 */
function messageKey(message: string): string {
  return message
    .replace(SYMBOL_PATTERN, 'TICKER')
    .replace(/\d+(?:[.,]\d+)?/g, 'N')
    .slice(0, 120)
}

/** Caller keys are stable strings; auto-generated ones look like notif-N-N. */
function sourceOf(key: string | undefined): string | null {
  return key && !key.startsWith('notif-') ? key : null
}

/** The symbols a toast names, so failing tickers stay visible next to the grouped key. */
function symbolsIn(message: string): string[] {
  const found = (message.match(SYMBOL_PATTERN) ?? []).map((t) => t.replace(/^\$/, ''))
  return [...new Set(found)].slice(0, MAX_SYMBOLS_PER_EVENT)
}

function reportErrorShown(message: string, key: string | undefined): void {
  const mk = messageKey(message)
  if (errorKeysSeen.has(mk) || errorsReported >= MAX_ERROR_SHOWN_PER_SESSION) return
  errorKeysSeen.add(mk)
  errorsReported += 1
  const symbols = symbolsIn(message)
  trackEvent(EVENTS.ERROR_SHOWN, {
    source: sourceOf(key),
    message_key: mk,
    ...(symbols.length > 0 && { symbols }),
  })
}

/** Test-only: reset the per-session counters. */
export function __resetErrorShownForTests(): void {
  errorsReported = 0
  errorKeysSeen.clear()
}

function createNotification(
  message: string,
  variant: NotificationVariant,
  props?: Partial<AppNotification>,
): AppNotification {
  return {
    key: `notif-${++notificationCounter}-${Date.now()}`,
    message,
    variant,
    autoHideDuration: 5000,
    ...props,
  }
}

export const MERGE_WINDOW_MS = 1000

let lastShownAt = 0
let buffered: { notification: AppNotification; count: number } | null = null
let windowTimer: ReturnType<typeof setTimeout> | undefined
let idleHandle: number | undefined

function cancelPending(): void {
  clearTimeout(windowTimer)
  windowTimer = undefined
  if (idleHandle !== undefined) cancelIdleCallback(idleHandle)
  idleHandle = undefined
  buffered = null
}

function isMergeable(n: AppNotification): boolean {
  return !!n.mergeGroup && n.variant !== 'error' && !n.action && !n.onClear && !n.persist
}

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => {
      const display = (notification: AppNotification, actionName: string) => {
        const prev = get().current
        if (prev?.onClear) prev.onClear()
        lastShownAt = Date.now()
        set({ current: notification }, undefined, actionName)
      }

      const flush = () => {
        idleHandle = undefined
        windowTimer = undefined
        const pending = buffered
        buffered = null
        if (!pending) return
        display(
          {
            ...pending.notification,
            message: `${pending.notification.message} (+${pending.count} more)`,
          },
          'flushNotification',
        )
      }

      const dispatch = (notification: AppNotification, actionName: string) => {
        const group = notification.mergeGroup
        const inWindow =
          isMergeable(notification) &&
          get().current?.mergeGroup === group &&
          Date.now() - lastShownAt < MERGE_WINDOW_MS
        if (!inWindow) {
          cancelPending()
          display(notification, actionName)
          return
        }
        buffered = { notification, count: (buffered?.count ?? 0) + 1 }
        if (windowTimer === undefined && idleHandle === undefined) {
          windowTimer = setTimeout(
            () => {
              windowTimer = undefined
              idleHandle = requestIdleCallback(flush, { timeout: MERGE_WINDOW_MS })
            },
            Math.max(0, MERGE_WINDOW_MS - (Date.now() - lastShownAt)),
          )
        }
      }

      return {
        current: null,
        showNotification: (notification) => {
          if (notification.variant === 'error')
            reportErrorShown(notification.message, notification.key)
          dispatch(notification, 'showNotification')
        },
        showSuccess: (message, options) =>
          dispatch(createNotification(message, 'success', options), 'showSuccess'),
        showError: (message, props) => {
          reportErrorShown(message, props?.key)
          dispatch(createNotification(message, 'error', props), 'showError')
        },
        showInfo: (message, options) =>
          dispatch(createNotification(message, 'info', options), 'showInfo'),
        showWarning: (message, options) =>
          dispatch(createNotification(message, 'warning', options), 'showWarning'),
        clearNotification: () => {
          cancelPending()
          lastShownAt = 0
          const prev = get().current
          if (prev?.onClear) prev.onClear()
          set({ current: null }, undefined, 'clearNotification')
        },
      }
    },
    { name: 'zustand/notification', store: 'notification', enabled: import.meta.env.DEV },
  ),
)
